import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import { Layout, Page, Card, TextField, Select, FormLayout, Button, DataTable, Checkbox, Banner, Badge, Link, Modal, BlockStack, Text, InlineStack } from '@shopify/polaris';
import LinesTable from '../../shared/components/linesTable';
import { TheSaveBar } from '../../shared/components/theSaveBar';
import { updateMyData } from '../../shared/util/updateLines';
import { handleSave, handleDiscard } from '../../shared/util/hanldesave';
import ReactSelect from 'react-select';
import { getRequest, postRequest, alphabetize, formatToCurrency } from '../../shared/components/functions.jsx';
import { useLookupStore } from '../../context/useStore.js';
import CompanySelect from '../../shared/components/companySelect';
import Toasts from '../../shared/components/toasts/toasts.jsx';
import { CardHeading } from '../../shared/components/cards/CardHeading';
import { getPageTitle, TabTitle } from '../../shell/helmet';
import EventTimeLine from '../../shared/components/events/eventTimeLine';

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const TIMING_OPTIONS = [
  { value: 'advance', label: 'In advance (invoice covers the coming period)' },
  { value: 'arrears', label: 'In arrears (invoice covers the past period)' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'ended', label: 'Ended' }
];

const RUN_STATUS_BADGES = {
  success: <Badge tone="success">Success</Badge>,
  failed: <Badge tone="critical">Failed</Badge>,
  pending: <Badge tone="warning">Pending</Badge>
};

//editable user picker (the shared OwnerSelect is display-only/disabled)
function NotifierSelect({ value, onChange, label }) {
  const users = useLookupStore((state) => state.users);
  const userOptions = users
    .filter((item) => ![true, 1, '1', 'true'].includes(item.Inactief))
    .sort((a, b) => alphabetize(a, b, 'Afkorting'))
    .map((item) => ({ value: item.GebruikerID, label: item.Gebruiker ? `${item.Afkorting} (${item.Gebruiker})` : item.Afkorting }));

  return (
    <div>
      <div style={{ marginBottom: '4px' }}>{label}</div>
      <ReactSelect
        id="NotifierSelect"
        placeholder=""
        options={userOptions}
        isClearable
        onChange={(option) => onChange(option ? option.value : null)}
        value={userOptions.find((option) => parseInt(option.value) === parseInt(value)) || null}
        menuPortalTarget={document.body}
        styles={{
          valueContainer: (base) => ({ ...base, height: '32px' }),
          indicatorsContainer: (base) => ({ ...base, height: '32px' }),
          control: (base) => ({ ...base, height: '32px', minHeight: '32px', borderRadius: '8px', borderColor: '#919191' }),
          menu: (provided) => ({ ...provided, zIndex: 9999 }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 })
        }}
      />
    </div>
  );
}

export default function RecurringInvoicePage() {
  let { id } = useParams();
  const pagination = useRecordPagination('recurring_invoices', id, 'recurring invoice');
  const navigate = useNavigate();
  const [template, setTemplate] = useState({
    Name: '',
    klantnummer: null,
    Frequency: 'monthly',
    IntervalCount: 1,
    StartDate: new Date().toISOString().split('T')[0],
    BillingTiming: 'advance',
    Status: 'active',
    NotifierUserID: null
  });
  const [initialTemplate, setInitialTemplate] = useState({});
  const [lines, setLines] = useState([]);
  const [initialLines, setInitialLines] = useState([]);
  const [runs, setRuns] = useState([]);
  const [company, setCompany] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [linesDirty, setLinesDirty] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [toast, setToast] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [isBlurred, setIsBlurred] = useState(true); //eslint-disable-line
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateForce, setGenerateForce] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const { languages, paymentconditions, businessunits } = useLookupStore();
  const pageTitle = getPageTitle(template.Name, 'Recurring invoice');

  const lineInterface = {
    lineIsDirty: false,
    RecurringInvoiceLineID: null,
    RecurringInvoiceID: id,
    produktnr: null,
    produkthulpnr: null,
    tekstlijn: '',
    VP: null,
    aantal: 1,
    BTW: company.LandID === 300 ? 21 : null,
    MuntVP: null,
    DiscountCode: null,
    SortOrder: 0
  };

  const fetchRuns = useCallback(async () => {
    if (id === 'new') return;
    const data = await getRequest(`/api/recurring_invoices/${id}/runs`);
    if (Array.isArray(data)) setRuns(data);
  }, [id]);

  useEffect(() => {
    async function fetchData() {
      if (id === 'new') return;
      const data = await getRequest(`/api/recurring_invoices/${id}`);
      if (!data) return;
      setTemplate(data);
      setInitialTemplate(data);
      const linesData = await getRequest(`/api/recurring_invoices/${id}/lines`);
      if (Array.isArray(linesData)) {
        setLines(linesData);
        setInitialLines(linesData);
      }
      if (data.klantnummer) {
        const companyData = await getRequest(`/api/companies/${data.klantnummer}`);
        if (companyData) setCompany(companyData);
      }
      fetchRuns();
    }
    fetchData();
  }, [id, fetchRuns]);

  const handleChange = (value, fieldId) => {
    setTemplate((template) => ({ ...template, [fieldId]: value }), setIsDirty(true));
  };

  const handleCompanyChange = async (value) => {
    const data = await getRequest(`/api/companies/${value}`);
    if (data) {
      setCompany(data);
      setTemplate(
        (template) => ({ ...template, klantnummer: data.FIRNR, TaalID: template.TaalID || data.TaalID, betalingID: template.betalingID != null ? template.betalingID : data.betalingID }),
        setIsDirty(true)
      );
    }
  };

  const saveForm = async () => {
    const payload = { ...template };
    //normalize empty strings to null for optional fields
    for (const field of ['EndDate', 'MaxOccurrences', 'TaalID', 'betalingID', 'BusinessUnitID', 'NotifierUserID', 'refklant', 'Opmerking']) {
      if (payload[field] === '') payload[field] = null;
    }
    await handleSave(
      id,
      'recurring_invoices',
      template.RecurringInvoiceID,
      payload,
      setTemplate,
      setInitialTemplate,
      setToast,
      setToastError,
      isDirty,
      setIsDirty,
      linesDirty,
      lines,
      setLines,
      setInitialLines,
      setLinesDirty,
      setSavingResult,
      navigate
    );
  };

  const generateNow = async () => {
    setGenerating(true);
    setGenerateMessage(null);
    const response = await fetch(`/api/recurring_invoices/${id}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ force: generateForce })
    });
    const data = await response.json().catch(() => ({}));
    setGenerating(false);
    if (response.ok) {
      setGenerateModalOpen(false);
      setGenerateForce(false);
      setToast(true);
      setGenerateMessage({ tone: 'success', text: data.msg || `Draft invoice ${data.Factuurnummer} generated.`, faktuurnr: data.faktuurnr });
      const refreshed = await getRequest(`/api/recurring_invoices/${id}`);
      if (refreshed) {
        setTemplate(refreshed);
        setInitialTemplate(refreshed);
      }
      fetchRuns();
    } else {
      setGenerateMessage({ tone: 'warning', text: data.msg || 'Could not generate the invoice.', faktuurnr: data.faktuurnr });
    }
  };

  const showPreview = async () => {
    const data = await postRequest(`/api/recurring_invoices/${id}/preview`, {});
    if (data) {
      setPreview(data);
      setPreviewModalOpen(true);
    }
  };

  const templateIsDue = template.NextRunDate && String(template.NextRunDate).substring(0, 10) <= new Date().toISOString().split('T')[0];

  const runRows = runs.map((run) => [
    run.RunDate ? String(run.RunDate).substring(0, 16).replace('T', ' ') : '',
    run.PeriodStart && run.PeriodEnd ? `${String(run.PeriodStart).substring(0, 10)} → ${String(run.PeriodEnd).substring(0, 10)}` : '',
    RUN_STATUS_BADGES[run.Status] || run.Status,
    run.TriggeredBy,
    run.faktuurnr ? (
      <Link removeUnderline url={`/invoices/${run.faktuurnr}`}>
        {run.Factuurnummer}
      </Link>
    ) : (
      ''
    ),
    run.totaalbedrag != null ? formatToCurrency(run.totaalbedrag) : '',
    run.faktuurnr ? run.Verstuurd ? <Badge tone="success">Sent</Badge> : <Badge tone="attention">To review</Badge> : '',
    run.ErrorMessage || ''
  ]);

  return (
    <Page
      pagination={pagination}
      title={id === 'new' ? 'New recurring invoice' : template.Name || 'Recurring invoice'}
      fullWidth
      backAction={{ content: 'Recurring invoices', url: '/recurring_invoices' }}
      titleMetadata={
        template.Status === 'active' ? (
          <Badge tone="success">Active</Badge>
        ) : template.Status === 'paused' ? (
          <Badge tone="warning">Paused</Badge>
        ) : template.Status === 'ended' ? (
          <Badge>Ended</Badge>
        ) : null
      }
      secondaryActions={
        id !== 'new'
          ? [
              { content: 'Preview next invoice', onAction: showPreview, disabled: isDirty || linesDirty },
              { content: 'Generate now', onAction: () => setGenerateModalOpen(true), disabled: isDirty || linesDirty || template.Status === 'ended' }
            ]
          : []
      }
    >
      <TabTitle title={pageTitle} />
      <Toasts toast={toast} setToast={setToast} errorToast={toastError} setErrorToast={setToastError} toastContent="Recurring invoice saved" />
      <TheSaveBar
        isDirty={isDirty || linesDirty}
        savingResult={savingResult}
        handleSave={saveForm}
        handleDiscard={() => handleDiscard(initialTemplate, initialLines, setTemplate, setLines, setIsDirty, setLinesDirty)}
      />

      {generateMessage && (
        <div style={{ marginBottom: 15 }}>
          <Banner tone={generateMessage.tone} onDismiss={() => setGenerateMessage(null)}>
            {generateMessage.text}{' '}
            {generateMessage.faktuurnr && (
              <Link removeUnderline url={`/invoices/${generateMessage.faktuurnr}`}>
                Open the invoice
              </Link>
            )}
          </Banner>
        </div>
      )}

      <Layout>
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <CardHeading title="Template" />
              <FormLayout>
                <TextField id="Name" label="Name (internal)" value={template.Name || ''} onChange={handleChange} autoComplete="off" placeholder="e.g. Hosting Acme - monthly" />
                <CompanySelect id="klantnummer" label="Customer" value={template.klantnummer} onChange={handleCompanyChange} company={company.FIRNR ? company : null} />
                <Select
                  id="TaalID"
                  label="Language (invoice)"
                  options={[
                    { value: '', label: 'Use customer language' },
                    ...languages.sort((a, b) => alphabetize(a, b, 'Omschrijving')).map((lan) => ({ value: String(lan.TaalID), label: lan.Omschrijving }))
                  ]}
                  value={template.TaalID != null ? String(template.TaalID) : ''}
                  onChange={(value) => handleChange(value === '' ? null : parseInt(value), 'TaalID')}
                />
                <Select
                  id="betalingID"
                  label="Payment condition"
                  options={[
                    { value: '', label: 'Use customer payment condition' },
                    ...paymentconditions.sort((a, b) => alphabetize(a, b, 'betalingNL')).map((cond) => ({ value: String(cond.betalingID), label: cond.betalingNL }))
                  ]}
                  value={template.betalingID != null ? String(template.betalingID) : ''}
                  onChange={(value) => handleChange(value === '' ? null : parseInt(value), 'betalingID')}
                />
                <Select
                  id="BusinessUnitID"
                  label="Business unit"
                  options={[{ value: '', label: 'None' }, ...businessunits.map((bu) => ({ value: String(bu.BusinessUnitID), label: `${bu.Code} - ${bu.Name}` }))]}
                  value={template.BusinessUnitID != null ? String(template.BusinessUnitID) : ''}
                  onChange={(value) => handleChange(value === '' ? null : parseInt(value), 'BusinessUnitID')}
                />
                <TextField
                  id="refklant"
                  label="Customer reference (placeholders allowed)"
                  value={template.refklant || ''}
                  onChange={handleChange}
                  autoComplete="off"
                  placeholder="e.g. Hosting {maand} {jaar}"
                />
                <TextField id="Opmerking" label="Remark on invoice (placeholders allowed)" value={template.Opmerking || ''} onChange={handleChange} autoComplete="off" multiline={2} />
              </FormLayout>
            </Card>

            <Card>
              <CardHeading title="Schedule" />
              <FormLayout>
                <FormLayout.Group>
                  <Select id="Frequency" label="Frequency" options={FREQUENCY_OPTIONS} value={template.Frequency} onChange={(value) => handleChange(value, 'Frequency')} />
                  <TextField
                    id="IntervalCount"
                    label="Every N periods"
                    type="number"
                    min={1}
                    value={String(template.IntervalCount || 1)}
                    onChange={(value) => handleChange(parseInt(value) || 1, 'IntervalCount')}
                    autoComplete="off"
                    helpText="1 = every period, 2 = every second period, ..."
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    id="StartDate"
                    label="Start date (billing day)"
                    type="date"
                    value={template.StartDate ? String(template.StartDate).substring(0, 10) : ''}
                    onChange={handleChange}
                    autoComplete="off"
                    helpText="The day of the month of this date is the billing day"
                  />
                  <TextField
                    id="EndDate"
                    label="End date (optional)"
                    type="date"
                    value={template.EndDate ? String(template.EndDate).substring(0, 10) : ''}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    id="MaxOccurrences"
                    label="Max. invoices (optional)"
                    type="number"
                    min={1}
                    value={template.MaxOccurrences != null ? String(template.MaxOccurrences) : ''}
                    onChange={(value) => handleChange(value === '' ? null : parseInt(value), 'MaxOccurrences')}
                    autoComplete="off"
                  />
                  <Select id="BillingTiming" label="Billing timing" options={TIMING_OPTIONS} value={template.BillingTiming} onChange={(value) => handleChange(value, 'BillingTiming')} />
                </FormLayout.Group>
                <Select
                  id="Status"
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={template.Status}
                  onChange={(value) => handleChange(value, 'Status')}
                  helpText="Paused templates are skipped by the daily run. Ended templates are finished permanently."
                />
                {id !== 'new' && (
                  <InlineStack gap="400">
                    <Text as="span" tone="subdued">
                      Next run: <b>{template.NextRunDate ? String(template.NextRunDate).substring(0, 10) : '-'}</b>
                    </Text>
                    <Text as="span" tone="subdued">
                      Generated: <b>{template.OccurrenceCount || 0}</b>
                    </Text>
                    <Text as="span" tone="subdued">
                      Last run: <b>{template.LastRunDate ? String(template.LastRunDate).substring(0, 10) : '-'}</b>
                    </Text>
                  </InlineStack>
                )}
              </FormLayout>
            </Card>

            <Card>
              <CardHeading title="Human check" />
              <FormLayout>
                <NotifierSelect label="Notifier (receives an email when an invoice is generated)" value={template.NotifierUserID} onChange={(value) => handleChange(value, 'NotifierUserID')} />
                <Banner tone="info">Generated invoices are drafts. They are never sent automatically: the notifier reviews each invoice and sends it through the normal invoice flow.</Banner>
              </FormLayout>
            </Card>

            <Card>
              <CardHeading title="Placeholders for dynamic lines" />
              <Text as="p" tone="subdued">
                Use these in line descriptions, the customer reference and the remark. They are filled in with the billing period when the invoice is generated (in the invoice language):
              </Text>
              <div style={{ marginTop: 8 }}>
                <DataTable
                  increasedTableDensity
                  columnContentTypes={['text', 'text']}
                  headings={[<b>Placeholder</b>, <b>Example (period July 2026, Dutch)</b>]}
                  rows={[
                    ['{maand} or {month}', 'juli'],
                    ['{maand+1} / {maand-1}', 'augustus / juni'],
                    ['{jaar} or {year}', '2026'],
                    ['{kwartaal} or {quarter}', 'Q3 2026'],
                    ['{periode} or {period}', '01/07/2026 - 31/07/2026']
                  ]}
                />
              </div>
            </Card>
          </BlockStack>
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <CardHeading title="Invoice lines (template)" />
              <LinesTable
                id={id}
                data={lines}
                updateMyData={updateMyData}
                setLines={setLines}
                setLinesDirty={setLinesDirty}
                onSubmit={saveForm}
                lineInterface={lineInterface}
                type="financial-simple"
                resource={{ ...template, Verstuurd: false }}
                company={company}
                disabled={savingResult}
                setIsDirty={setIsDirty}
                setIsBlurred={setIsBlurred}
              />
            </Card>

            {id !== 'new' && (
              <Card>
                <CardHeading title={`Generation history (${runs.length})`} />
                {runs.length > 0 ? (
                  <DataTable
                    stickyHeader
                    increasedTableDensity
                    verticalAlign="middle"
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
                    headings={[<b>Run date</b>, <b>Period</b>, <b>Status</b>, <b>Trigger</b>, <b>Invoice</b>, <b>Amount</b>, <b>Invoice sent?</b>, <b>Error</b>]}
                    rows={runRows}
                  />
                ) : (
                  <Text as="p" tone="subdued">
                    No invoices have been generated from this template yet.
                  </Text>
                )}
              </Card>
            )}

            {id !== 'new' && <EventTimeLine resourceName="RECURRING_INVOICE" resourceId={id} />}
          </BlockStack>
        </Layout.Section>
      </Layout>

      <Modal
        open={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        title="Generate an invoice now"
        primaryAction={{ content: 'Generate draft invoice', onAction: generateNow, loading: generating }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setGenerateModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">A draft invoice will be created from this template. It will NOT be sent to the customer; the notifier gets an email to review it.</Text>
            {templateIsDue ? (
              <Banner tone="info">
                The next invoice (scheduled {String(template.NextRunDate).substring(0, 10)}) is due and will be generated for its normal billing period. The schedule moves forward.
              </Banner>
            ) : (
              <Banner tone="warning">
                The next invoice is only due on {template.NextRunDate ? String(template.NextRunDate).substring(0, 10) : '-'}. To generate now anyway, enable the test option below.
              </Banner>
            )}
            <Checkbox
              label="Force generate (testing): create the invoice now without affecting the schedule"
              checked={generateForce}
              onChange={setGenerateForce}
              helpText="Use this to test the template. The scheduled invoice for this period will still be generated on its normal date, so remember to credit/delete test invoices."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Preview of the next invoice"
        secondaryActions={[{ content: 'Close', onAction: () => setPreviewModalOpen(false) }]}
      >
        <Modal.Section>
          {preview && (
            <BlockStack gap="300">
              <Text as="p">
                Scheduled date: <b>{preview.scheduledDate}</b> — Billing period: <b>{preview.periodStart}</b> → <b>{preview.periodEnd}</b>
              </Text>
              {preview.refklant && (
                <Text as="p">
                  Customer reference: <b>{preview.refklant}</b>
                </Text>
              )}
              <DataTable
                increasedTableDensity
                columnContentTypes={['numeric', 'text', 'numeric', 'numeric']}
                headings={[<b>Qty</b>, <b>Description (resolved)</b>, <b>VP</b>, <b>VAT %</b>]}
                rows={(preview.lines || []).map((l) => [l.aantal, l.tekstlijn, l.VP != null ? formatToCurrency(l.VP) : '', l.BTW])}
              />
              <Banner tone="info">This is only a preview, nothing has been generated.</Banner>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
